import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { Label } from "@fluentui/react/lib/Label";
import { Stack } from "@fluentui/react/lib/Stack";

import { useAuthenticationPreflight } from "Components/Generic/Authentication";
import { IconButtonWithTooltip } from "Components/Generic/IconButtonWithTooltip";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { WithTranslator } from "Components/Generic/Translator";
import { ValueWithTooltip } from "Components/Generic/ValueWithTooltip";
import { EntityType } from "Model/EntityType";
import { projectsStagingManageRemoveStart } from "Store/Actions";
import { getTranslatedFieldLabel } from "Utils/Field";
import { useEntryStyles } from "./UseEntryStyles";
import { IEntryProps } from "./IEntryProps";

export const Entry = WithTheme(
  WithTranslator((props: IEntryProps): JSX.Element => {
    const {
      fieldName,
      item,
      translator,
      theme: { spacing }
    } = props;
    const dispatch = useDispatch();
    const { preflightAuthentication } = useAuthenticationPreflight();
    const onClick = useCallback(async () => {
      await preflightAuthentication();
      dispatch(projectsStagingManageRemoveStart(fieldName));
    }, [dispatch, fieldName, preflightAuthentication]);
    const translatedLabel = getTranslatedFieldLabel(fieldName, EntityType.Project, translator);
    const styles = useEntryStyles();

    return (
      <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: spacing.m }} className={styles.root}>
        <Stack.Item disableShrink className={styles.action}>
          <IconButtonWithTooltip
            onClick={onClick}
            iconProps={{ iconName: `Cancel` }}
            tooltip={translator.get(`staging.ignored.delete`, `Remove`)}
          />
        </Stack.Item>
        <Label className={styles.label} title={`${translatedLabel} (${fieldName})`}>
          {getTranslatedFieldLabel(fieldName, EntityType.Project, translator)}
        </Label>
        <Stack.Item grow className={styles.value}>
          <ValueWithTooltip item={item} fieldName={fieldName} entityType={EntityType.Project} />
        </Stack.Item>
      </Stack>
    );
  })
);
